<script setup lang="ts">
/**
 * HubAskBar — the Hub's primary action: a single line you can type into.
 *
 * The Hub used to be a brochure (hero headline + paragraph + a grid of
 * tiles) where the fastest route to an answer was read → pick a tile →
 * land in chat → type. This collapses that to one step: land, type, Enter.
 * The bar routes to `#/chat/<slug>?q=<question>`, which App.vue already
 * parses and auto-submits, so no new plumbing on the chat side.
 *
 * The chip on the left is the shared AssistantChip — the same control the
 * chat header uses — so the two surfaces cannot drift into two different
 * dropdown languages. It names the assistant the question will go to and
 * opens the menu. Default target is the user's
 * `preference.default_assistant_slug`, else a `default`-slug assistant if
 * they can see one, else the first in the list, so the bar is never
 * ambiguous about where the question lands.
 *
 * Renders nothing when the caller has no assistants (the Hub shows its
 * empty state instead).
 */
import { computed, ref, watch } from "vue";
import type { Assistant } from "../api/conversations";
import AssistantChip from "./AssistantChip.vue";

const props = defineProps<{
  assistants: Assistant[];
  /** `preference.default_assistant_slug` from /api/profile, when set. */
  preferredSlug?: string | null;
}>();

/** Which assistant the typed question is addressed to. */
const targetSlug = ref("");

/** Pick the initial target once the list (and the profile) have landed. */
watch(
  () => [props.assistants, props.preferredSlug] as const,
  ([list, preferred]) => {
    if (list.length === 0) return;
    const has = (slug: string) => list.some((a) => a.slug === slug);
    if (targetSlug.value && has(targetSlug.value)) return;
    if (preferred && has(preferred)) targetSlug.value = preferred;
    else if (has("default")) targetSlug.value = "default";
    else targetSlug.value = list[0].slug;
  },
  { immediate: true, deep: true },
);

const question = ref("");
const canSend = computed(
  () => question.value.trim().length > 0 && !!targetSlug.value,
);

function send(): void {
  const q = question.value.trim();
  if (!q || !targetSlug.value) return;
  window.location.hash = `#/chat/${targetSlug.value}?q=${encodeURIComponent(q)}`;
  question.value = "";
}
</script>

<template>
  <div v-if="assistants.length > 0" class="ask">
    <div class="ask__bar glass-strong">
      <AssistantChip v-model="targetSlug" :assistants="assistants" />

      <input
        v-model="question"
        type="text"
        class="ask__input"
        placeholder="Ask anything…"
        aria-label="Ask a question"
        @keydown.enter="send"
      />

      <button
        type="button"
        class="ask__send"
        :disabled="!canSend"
        aria-label="Send question"
        @click="send"
      >
        <span aria-hidden="true">→</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.ask {
  position: relative;
}

.ask__bar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 0.5rem 0.5rem 0.5rem 0.65rem;
  border-radius: var(--radius-pill);
  transition:
    box-shadow var(--dur-2) var(--ease-out),
    border-color var(--dur-2) var(--ease-out);
}
.ask__bar:focus-within {
  border-color: color-mix(
    in srgb,
    var(--color-accent) 40%,
    var(--glass-border)
  );
  box-shadow: var(--shadow-glow);
}

.ask__input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--color-ink);
  font-size: 1.02rem;
  line-height: 1.4;
  padding: 0.35rem 0;
}
/* The bar owns the focus treatment (`:focus-within` above) — the app-wide
   ring on the borderless input would draw a second, floating rectangle
   inside it. */
.ask__input:focus,
.ask__input:focus-visible {
  outline: none;
  box-shadow: none;
}
.ask__input::placeholder {
  color: var(--color-muted);
}

.ask__send {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 50%;
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
  font-size: 1.05rem;
  cursor: pointer;
  transition:
    background var(--dur-1) var(--ease-out),
    opacity var(--dur-1) var(--ease-out),
    transform var(--dur-1) var(--ease-spring);
}
.ask__send:hover:not(:disabled) {
  background: var(--color-accent-hover);
  transform: scale(1.06);
}
.ask__send:disabled {
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--color-muted);
  cursor: default;
}

@media (max-width: 560px) {
  .ask__bar {
    flex-wrap: wrap;
    border-radius: var(--radius-lg);
    padding: 0.55rem;
  }
  .ask__input {
    order: -1;
    width: 100%;
    flex-basis: 100%;
    padding: 0.4rem 0.35rem;
  }
  .ask__send {
    margin-left: auto;
  }
}
</style>
